/* PREVIEW DECK — the whole presentation, one slide at a time, full screen.
 *
 * Specced in docs/presentations-spec.md ("dark overlay, one slide, close
 * top-right, prev/next") and never built until 2026-09-23; her ask that day:
 * "the preview button for presentations to work and show a slide deck".
 *
 * It draws with SlidePreview, which draws from the same shapes the .pptx is
 * written from — so what she steps through here is what she downloads. Nothing
 * here decides layout, and nothing here writes: it is a viewer.
 *
 * Keys: → / Space / PageDown next, ← / PageUp back, Esc closes. Speaker notes
 * sit under the slide behind a toggle, because rehearsing is half of why
 * anyone steps through a deck before giving it.
 */
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { SLIDE_H, SLIDE_W } from "../../utils/deckLayout";
import { SlidePreview } from "./slidePreview";

/* The biggest 16:9 that fits, leaving room for the bar above and the controls
   below. Recomputed on resize so a rotated phone gets the bigger slide. */
function fitWidth(notes) {
  if (typeof window === "undefined") return 960;
  const w = window.innerWidth - 32;
  const h = window.innerHeight - (notes ? 260 : 140);
  return Math.max(200, Math.floor(Math.min(w, (h * SLIDE_W) / SLIDE_H)));
}

/**
 * @param slides  deckSlides(sections, slides) — [{ type, content, notes }] in deck order
 * @param skin    the RESOLVED skin (resolveSkin), same as the rows use
 */
export function DeckPreview({ title, slides = [], skin, design, onClose, start = 0 }) {
  const [i, setI] = useState(Math.min(start, Math.max(slides.length - 1, 0)));
  const [notes, setNotes] = useState(false);
  const [width, setWidth] = useState(() => fitWidth(false));
  const last = slides.length - 1;
  const go = (n) => setI((cur) => Math.max(0, Math.min(last, typeof n === "function" ? n(cur) : n)));

  useEffect(() => {
    const onResize = () => setWidth(fitWidth(notes));
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [notes]);

  useEffect(() => {
    /* Clamped here rather than through `go`, so the effect depends only on
       what it reads and re-binds only when the deck's length changes. */
    const step = (d) => setI((c) => Math.max(0, Math.min(last, c + d)));
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (["ArrowRight", "PageDown", " "].includes(e.key)) { e.preventDefault(); step(1); }
      else if (["ArrowLeft", "PageUp"].includes(e.key)) { e.preventDefault(); step(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [last, onClose]);

  const slide = slides[i];
  const ctl = (disabled) => ({
    background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.18)", color: "#fff",
    borderRadius: 999, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center",
    cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.3 : 1,
  });

  return (
    <div role="dialog" aria-modal="true" aria-label={`Preview of ${title}`}
      /* SOLID, not a dim: the first build let the page show through at 0.96, and
         the editor behind a slide read as part of it. */
      style={{ position: "fixed", inset: 0, zIndex: 120, background: "#0b0b12", display: "flex", flexDirection: "column", alignItems: "center", color: "#fff" }}>
      <div style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", boxSizing: "border-box" }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
        <button onClick={() => setNotes((v) => !v)} aria-pressed={notes}
          style={{ background: notes ? "rgba(255,255,255,0.16)" : "none", border: "1px solid rgba(255,255,255,0.25)", color: "#fff", borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
          Notes
        </button>
        <button onClick={onClose} aria-label="Close preview" style={{ ...ctl(false), width: 36, height: 36 }}><X size={18} /></button>
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, minHeight: 0, padding: "0 16px" }}>
        {slide ? (
          /* Tap the left third to go back, anywhere else to go on — a phone has
             no arrow keys, and 40px buttons are not the only way through. */
          <div onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            go((c) => c + (e.clientX - r.left < r.width / 3 ? -1 : 1));
          }} style={{ cursor: "pointer" }}>
            <SlidePreview slide={{ type: slide.type, content: slide.content }} skin={skin} design={design} width={width}
              label={`Slide ${i + 1} of ${slides.length}`} />
          </div>
        ) : (
          <div style={{ fontSize: 14, opacity: 0.7 }}>No slides yet.</div>
        )}
        {notes && slide && (
          <div style={{ width, maxWidth: "100%", maxHeight: 110, overflowY: "auto", fontSize: 13.5, lineHeight: 1.6, color: "rgba(255,255,255,0.85)", whiteSpace: "pre-wrap" }}>
            {String(slide.notes || "").trim() || <span style={{ opacity: 0.55 }}>No speaker notes on this slide.</span>}
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 18, padding: "14px 16px 20px" }}>
        <button onClick={() => go((c) => c - 1)} disabled={i <= 0} aria-label="Previous slide" style={ctl(i <= 0)}><ChevronLeft size={20} /></button>
        <span style={{ fontSize: 13, minWidth: 64, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{slides.length ? `${i + 1} / ${slides.length}` : "0 / 0"}</span>
        <button onClick={() => go((c) => c + 1)} disabled={i >= last} aria-label="Next slide" style={ctl(i >= last)}><ChevronRight size={20} /></button>
      </div>
    </div>
  );
}
